import { Fragment, useEffect, useMemo, useRef } from "react";
import { Edges } from "@react-three/drei";
import * as THREE from "three";
import { buildEspPipe, buildBelt, buildElbowPipe } from "../Utils/geometryUtils";

import { buildDuct, buildESPConeGeometry, buildHollowFrame, buildPanel } from "../Utils/geometryUtils";
import { fromMM } from "../Utils/frameUtils";
import ESPProperties from "./Properties/ESPProperties";
import { align } from "../Utils/frameUtils";
import { COMPONENT_ALIGNMENT, CONNECTION_LENGTH, ACCESS_SIDE } from "../Constants";
import { useStore } from "../Store/zustandStore";

// Lip flange on each end of the ESP body. Drawn by ESPUnit and used by the
// cone, which lands on the outer face of the AHU-facing one - defined once
// here so the two cannot drift apart.
const ESP_LIP_MM = 20;

export default function ESP({ frame }) {
  const [frameThickness] = useStore((state) => [fromMM(state.frameThickness)]);

  const { esp } = frame;
  const { height: frameBaseFrameHeight } = frame.baseFrame;

  const { accessSide } = frame;

  const backControlBoxWidth = fromMM(esp?.backControlBoxWidth || ESPProperties.backControlBoxWidth);

  const isESPExterior = esp?.isESPExterior ?? false;
  const coneDistance = esp?.coneDistance ?? fromMM(500);
  const hasFeet = esp?.hasFeet ?? false;
  const hasDrain = esp?.hasDrain ?? false;
  const feetHeight = esp?.feetHeight ?? 0;

  const width = fromMM(esp?.width || ESPProperties.width) - backControlBoxWidth;
  const height = fromMM(esp?.height || ESPProperties.height);

  // Double-pass: render two ESP stages back-to-back along the airflow axis with
  // one shared cone at the inlet.
  // backend at single-stage values; the renderer doubles them so we draw two
  const passCount = esp?.isDoublePass ? 2 : 1;
  const length = fromMM(esp?.length || ESPProperties.length) * passCount;
  const columns = (esp?.columns || ESPProperties.columns) * passCount;

  const rows = esp?.rows || ESPProperties.rows;

  const frameWidth = fromMM(frame.width);
  const frameHeight = fromMM(frame.height);
  const frameLength = fromMM(frame.length);

  const hasAutoWasher = esp?.hasAutoWasher != undefined ? esp.hasAutoWasher : ESPProperties.hasAutoWasher;

  const controlBoxWidth = fromMM(esp?.controlBoxWidth || ESPProperties.controlBoxWidth);
  const controlBoxHeight = fromMM(esp?.controlBoxHeight || ESPProperties.controlBoxHeight);
  const controlBoxLength = fromMM(esp?.controlBoxLength || ESPProperties.controlBoxLength);

  const backControlBoxHeight = fromMM(esp?.backControlBoxHeight || ESPProperties.backControlBoxHeight);
  const backControlBoxLength = fromMM(esp?.backControlBoxLength || ESPProperties.backControlBoxLength);

  const autoWasherBoxWidth = fromMM(esp?.autoWasherBoxWidth || ESPProperties.autoWasherBoxWidth);
  const autoWasherBoxHeight = fromMM(esp?.autoWasherBoxHeight || ESPProperties.autoWasherBoxHeight);
  const autoWasherBoxLength = fromMM(esp?.autoWasherBoxLength || ESPProperties.autoWasherBoxLength);
  const autoWasherBoxDistance = fromMM(500);

  //const pipeWidth = autoWasherBoxLength * 1.25;
  const pipeWidth = autoWasherBoxLength;
  const pipeLength = autoWasherBoxWidth;
  const pipeHeight = autoWasherBoxHeight;
  const tubeThickness = 0.025;
  const intrudingLength = frameWidth / 4;

  const baseFrameLength = isESPExterior ? 0 : length * 1.1;
  const baseFrameHeight = isESPExterior ? 0 : fromMM(30);
  const baseFrameWidth = isESPExterior ? 0 : isESPExterior ? width * 1.1 : frameWidth;

  const totalLength = fromMM(length) + fromMM(coneDistance || 0);
  const espXOffset = align(0, fromMM(frame.length), totalLength, frame.componentAlignment) - fromMM(coneDistance || 0) / 2;

  const boxFrameThickness = fromMM(esp?.frame || ESPProperties.frame);

  const widthAlignment = isESPExterior ? COMPONENT_ALIGNMENT.CENTER : COMPONENT_ALIGNMENT.CENTER;

  const position = [
    align(0, frameLength, length, frame.componentAlignment) - fromMM(coneDistance || 0) / 2,
    align(0, frameHeight, height + baseFrameHeight * 2, COMPONENT_ALIGNMENT.BOTTOM) + (feetHeight > 0 ? feetHeight - fromMM(100) : 0),
    align(0, frameWidth, width + backControlBoxWidth * 2, widthAlignment),
  ];

  //remove framethickness
  if (isESPExterior) {
    position[0] += frameThickness / 2;
    position[1] -= frameThickness;
  }

  const backControlBoxGeom = useMemo(() => {
    return buildPanel(backControlBoxLength, backControlBoxHeight, backControlBoxWidth);
  }, [backControlBoxLength, backControlBoxHeight, backControlBoxWidth]);

  const autoWasherBoxGeom = useMemo(() => {
    return buildPanel(autoWasherBoxLength, autoWasherBoxHeight, autoWasherBoxWidth);
  }, [autoWasherBoxLength, autoWasherBoxHeight, autoWasherBoxWidth]);

  const bottom_offset = fromMM(150);

  // Outlet flange: the 50mm collar joining the AHU casing to the cone's wide
  // end, so the cone lands on a flange rather than on the bare frame - the
  // same collar BoxFan.js and PRLTCFan.js draw on the discharge side. Wherever
  // a cone connects an external component to the unit, that joint gets a
  // flange, and for an external ESP the cone is on the INLET side.
  //
  // buildESPConeGeometry mirrors X, and the ESP cone's wide end is written
  // NEGATIVE (-extendedLength - frameLength / 2), so it actually renders at
  // the positive AHU-facing bound. The flange is placed in unmirrored world
  // space, hence the negation here, and it runs from that wall back toward the
  // cone (decreasing X) - the mirror of the fan side.
  // The collar's BORE must equal the frame opening, not exceed it: sizing the
  // outer face to frame + proud and then hollowing it by a thin wall left a
  // bore wider than the cone's wide end, so the cone slid straight through the
  // opening and hid the flange instead of seating against it. Building it as
  // (opening + 2 * proud) with the wall = proud puts the bore exactly on the
  // opening, so the collar is a visible rim standing proud all the way round.
  const OUTLET_FLANGE_MM = 50;
  const outletFlangeLength = fromMM(OUTLET_FLANGE_MM);
  const outletFlangeProud = fromMM(15);
  const outletFlangeWallThickness = outletFlangeProud;
  // The ESP body carries its own lip flange on each end (drawn by ESPUnit).
  // The cone lands on the outer face of the AHU-facing one.
  const espLipLength = fromMM(ESP_LIP_MM);
  const coneExtendedLength = isESPExterior ? frameThickness : 0;
  const ahuWallX = coneExtendedLength + frameLength / 2;
  const outletFlangeX = ahuWallX - outletFlangeLength / 2;
  const outletFlangeGeom = useMemo(
    () =>
      buildHollowFrame(
        outletFlangeLength,
        frameHeight + outletFlangeProud * 2,
        frameWidth + outletFlangeProud * 2,
        outletFlangeWallThickness,
      ),
    [outletFlangeLength, frameHeight, frameWidth, outletFlangeProud, outletFlangeWallThickness],
  );

  const coneGeom = useMemo(() => {
    const extendedLength = isESPExterior ? frameThickness : 0;
    // Narrow end lands on the OUTER face of the ESP's own lip flange, so the
    // cone attaches to that flange instead of swallowing it.
    //
    // This is derived from position[0] - where the ESP body is actually drawn
    // (ESPUnits is rendered at `position`) - NOT from espXOffset. The two are
    // computed differently: espXOffset aligns the body PLUS the cone
    // (totalLength) and position[0] aligns the body alone, and position[0]
    // additionally gets the +frameThickness/2 exterior nudge that espXOffset
    // never receives. Anchoring the cone to espXOffset therefore left the
    // narrow end a couple of hundred mm off the body, which is what buried
    // the lip. Written pre-mirror (negated), hence the leading minus.
    //
    // The lip is the ESP's own flange, not part of the transition, so the
    // cone stops short of it and its length excludes it.
    const leftPositionX = -(position[0] + length / 2 + espLipLength);
    // Wide end starts at the outlet flange's downstream face rather than on
    // the bare frame wall. Written pre-mirror (negated), so stepping inward
    // off the wall ADDS the flange length here. Only an exterior ESP gets the
    // flange, so an interior one keeps its cone exactly where it was.
    const coneWidePreMirrorX =
      -extendedLength - frameLength / 2 + (isESPExterior ? outletFlangeLength : 0);
    // coneDistance > 0
    //   ? fromMM(coneDistance) > length / 2
    //     ? fromMM(coneDistance) - length
    //     : fromMM(coneDistance) - length / 2 - frameThickness
    //   : -length / 2;

    return buildESPConeGeometry(
      [
        [
          new THREE.Vector3(coneWidePreMirrorX, frameHeight / 2, -frameWidth / 2),
          frameWidth,
          0,
          new THREE.Vector3(
            leftPositionX,
            //   espGroupPosition[1] +
            position[1] + (height - boxFrameThickness * 2) / 2,
            position[2] - (width - boxFrameThickness * 2) / 2,
          ),
          width - boxFrameThickness * 2,
          0,
        ],
        // bottom
        [
          new THREE.Vector3(coneWidePreMirrorX, -frameHeight / 2, -frameWidth / 2),
          frameWidth,
          0,
          new THREE.Vector3(
            leftPositionX,
            //  espGroupPosition[1] +
            position[1] - (height - boxFrameThickness * 2) / 2,
            position[2] - (width - boxFrameThickness * 2) / 2,
          ),
          width - boxFrameThickness * 2,
          0,
        ],
        // back
        [
          new THREE.Vector3(coneWidePreMirrorX, frameHeight / 2, -frameWidth / 2),
          0,
          -frameHeight,
          new THREE.Vector3(
            leftPositionX,
            //   espGroupPosition[1] +
            position[1] + (height - boxFrameThickness * 2) / 2,
            position[2] - (width - boxFrameThickness * 2) / 2,
          ),
          0,
          -(height - boxFrameThickness * 2),
        ],
        // front
        [
          new THREE.Vector3(coneWidePreMirrorX, frameHeight / 2, frameWidth / 2),
          0,
          -frameHeight,
          new THREE.Vector3(
            leftPositionX,
            //  espGroupPosition[1] +
            position[1] + (height - boxFrameThickness * 2) / 2,
            position[2] + (width - boxFrameThickness * 2) / 2,
          ),
          0,
          -(height - boxFrameThickness * 2),
        ],
      ],
      !isESPExterior,
    );
    // position[] drives all three axes of the narrow end, so the values it is
    // derived from have to be listed or the cone goes stale when the ESP moves.
  }, [frameHeight, frameLength, frameWidth, isESPExterior, height, width, length, outletFlangeLength, espLipLength, position[0], position[1], position[2]]);

  // const autowashPipeGeom = useMemo(() => {
  //   return buildEspPipe(pipeWidth, pipeHeight, autoWasherBoxDistance + pipeLength, tubeThickness, autoWasherBoxLength / 2, width / 2 - 0.1);
  // }, [pipeWidth, pipeHeight, tubeThickness, autoWasherBoxWidth, frameThickness]);
  const accessFactor = accessSide == ACCESS_SIDE.LEFT ? -1 : 1;
  const connectorX = -length / 2 - fromMM(coneDistance / 2) + 0.05;
  const connectorY = 0.05 - height / 2;
  const connectorZ = accessFactor * (position[2] - width / 2 - 0.05);
  const floorY = position[1] - height / 2 - (hasFeet ? feetHeight : 0);
  
  const autowasherBoxPosition = [
    connectorX,
    floorY + autoWasherBoxHeight / 2,
    connectorZ - accessFactor * autoWasherBoxDistance,
  ];

  const stubPoint = [connectorX, connectorY, connectorZ];
  const boxTopPoint = [
    autowasherBoxPosition[0],
    autowasherBoxPosition[1] + autoWasherBoxHeight / 2,
    autowasherBoxPosition[2],
  ];
  const cornerPoint = [stubPoint[0], stubPoint[1], boxTopPoint[2]];

  const autowashPipeGeom = useMemo(() => {
    return buildElbowPipe(stubPoint, cornerPoint, boxTopPoint, tubeThickness);
  }, [
    stubPoint[0], stubPoint[1], stubPoint[2],
    cornerPoint[0], cornerPoint[1], cornerPoint[2],
    boxTopPoint[0], boxTopPoint[1], boxTopPoint[2],
    tubeThickness,
  ]);


  const espGroup = useRef();

  // The ESP body and its inlet cone sit on the airflow (X) axis and are built
  // symmetrically about the frame centre, so the access side must never rotate
  // them - doing so swings the cone off the duct connector to the filter section.
  // Access side only mirrors the side-mounted hardware across Z.
  useEffect(() => {
    if (espGroup.current) {
      espGroup.current.rotation.set(0, 0, 0);
      espGroup.current.updateMatrix();
    }
  }, [accessSide]);

  return (
    <group dispose={null} ref={espGroup} userData={{ excludeProjections: [] }} name='esp'>
      {/* Outlet flange: 50mm collar joining the AHU casing to the cone. Only
          an exterior ESP connects through a cone, so only it gets the flange. */}
      {isESPExterior && (
        <mesh geometry={outletFlangeGeom} position={[outletFlangeX, 0, 0]} userData={{ excludeProjections: ["left", "right"] }}>
          <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>
      )}

      <mesh geometry={coneGeom} userData={{ excludeProjections: ["left", "right"] }}>
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>
      {/*<group position={espGroupPosition}> */}
      <group>
        {hasAutoWasher && (
          <group>
            {/* <mesh geometry={autoWasherBoxGeom} position={autoWasherBoxPosition}>
              <meshStandardMaterial color={"Grey"} side={THREE.DoubleSide} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="Grey" widthTest={true} />
              </Edges>
            </mesh> */}
            <mesh geometry={autoWasherBoxGeom} position={autowasherBoxPosition} userData={{ excludeProjections: ["side"] }}>
              <meshStandardMaterial color={"Grey"} side={THREE.DoubleSide} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="Grey" widthTest={true} />
              </Edges>
            </mesh>
            <mesh geometry={autowashPipeGeom} receiveShadow castShadow userData={{ excludeProjections: ["side"] }}>
              <meshStandardMaterial color="Black" side={THREE.DoubleSide} />
            </mesh>
            <mesh
              geometry={buildPanel(0.1, 0.1, 0.1)}
              position={[connectorX, connectorY, connectorZ]}
              userData={{ excludeProjections: ["side"] }}
            >
              <meshStandardMaterial color={"Grey"} side={THREE.DoubleSide} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="Grey" widthTest={true} />
              </Edges>
            </mesh>

            <mesh
              geometry={buildPanel(0.1, 0.1, 0.1)}
              position={[connectorX, connectorY, connectorZ]}
              userData={{ excludeProjections: ["side"] }}
            >
              <meshStandardMaterial color={"Grey"} side={THREE.DoubleSide} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="Grey" widthTest={true} />
              </Edges>
            </mesh>

          </group>
        )}
        {/* <mesh
          geometry={backControlBoxGeom}
          position={[
            //espXOffset,
            position[0],
            position[1],
            position[2] - width / 2 - backControlBoxWidth / 2,
          ]}
        >
          <meshStandardMaterial color={"Grey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh> */}
        <ESPUnits
          position={[position[0], position[1], position[2]]}
          verticalCount={rows}
          horizontalCount={columns}
          width={width}
          height={height}
          length={length}
          controlBoxLength={controlBoxLength}
          controlBoxHeight={controlBoxHeight}
          controlBoxWidth={controlBoxWidth}
          boxFrameThickness={boxFrameThickness}
          frameHeight={frameHeight}
          frameThickness={frameThickness}
          align={align}
          isESPExterior={isESPExterior}
          hasFeet={hasFeet}
          feetHeight={feetHeight}
          baseFrameLength={baseFrameLength}
          baseFrameHeight={baseFrameHeight}
          baseFrameWidth={baseFrameWidth}
          hasDrain={hasDrain}
          accessFactor={accessFactor}
        ></ESPUnits>

        {!hasFeet && (
          <mesh position={[espXOffset, align(0, frameHeight, baseFrameHeight, COMPONENT_ALIGNMENT.BOTTOM), 0]}>
            <boxGeometry args={[baseFrameLength, baseFrameHeight, baseFrameWidth]} />
            <meshStandardMaterial color={"Grey"} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        )}
      </group>
    </group>
  );
}

function ESPUnits({
  position,
  verticalCount = 1,
  horizontalCount = 1,
  width,
  height,
  length,
  controlBoxLength,
  controlBoxHeight,
  controlBoxWidth,
  boxFrameThickness,
  baseFrameLength,
  baseFrameHeight,
  baseFrameWidth,
  isESPExterior,
  frameThickness,
  feetHeight,
  hasFeet,
  frameHeight,
  hasDrain,
  accessFactor = 1,
}) {
  const espUnits = [];
  const espLength = length / horizontalCount;
  const espHeight = height / verticalCount;
  let initialY = align(0, height, espHeight, COMPONENT_ALIGNMENT.BOTTOM);
  let initialX = align(0, length, espLength, COMPONENT_ALIGNMENT.LEFT);
  for (let h = 0; h < horizontalCount; h++)
    for (let v = 0; v < verticalCount; v++) {
      const x = initialX + h * espLength;
      const y = initialY + v * espHeight;
      const isBottomRow = v === 0;
      espUnits.push(
        <>
          <ESPUnit
            position={[initialX + h * espLength, initialY + v * espHeight, 0]}
            width={width}
            height={espHeight}
            length={espLength}
            controlBoxLength={controlBoxLength}
            controlBoxHeight={controlBoxHeight / 2}
            controlBoxWidth={controlBoxWidth}
            boxFrameThickness={boxFrameThickness}
            accessFactor={accessFactor}
          />
          {hasFeet && (
            <Feet
              //  baseFrameLength={baseFrameLength}
              //  baseFrameHeight={baseFrameHeight}
              //  baseFrameWidth={baseFrameWidth}
              frameHeight={height}
              frameThickness={frameThickness}
              align={align}
              isEspExterior={isESPExterior}
              espUnitPosition={[x, y, 0]}
              espUnitWidth={width}
              espUnitLength={espLength}
              espUnitHeight={espHeight}
              isBottomRow={isBottomRow}
              verticalCount={verticalCount}
              horizontalCount={horizontalCount}
              height={height}
              feetHeight={feetHeight}
            />
          )}
          {isBottomRow && hasDrain && (
            <ESPDrain
              baseFrameLength={baseFrameLength}
              baseFrameHeight={baseFrameHeight}
              baseFrameWidth={baseFrameWidth}
              frameHeight={frameHeight}
              frameThickness={frameThickness}
              align={align}
              IsEspExterior={isESPExterior}
              espUnitPosition={[x, y, 0]}
              espUnitWidth={width}
              espUnitLength={espLength}
              espUnitHeight={espHeight}
              isBottomRow={isBottomRow}
              verticalCount={verticalCount}
              horizontalCount={horizontalCount}
              height={height}
              accessFactor={accessFactor}
            />
          )}
        </>,
      );
    }

  return (
    <group position={position}>
      {/* //<group position={[position[0], position[1] - fromMM(frameThickness), position[2]]}> */}
      {espUnits.map((esp, i) => (
        <Fragment key={i}>{esp}</Fragment>
      ))}
    </group>
  );
}

function ESPUnit({ position, width, height, length, controlBoxLength, controlBoxHeight, controlBoxWidth, boxFrameThickness, accessFactor = 1 }) {
  const controlBoxFrontPanel = fromMM(10);
  const controlBoxFrontPanelWhite = controlBoxLength * 0.75;
  const controlBoxRedKnobRadius = fromMM(20);
  const controlBoxDial = fromMM(40);

  const topCylinderRadius = fromMM(20);

  const lip = fromMM(ESP_LIP_MM);
  var extrudeSettings = {
    depth: lip,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };

  const lipGeom = useMemo(() => {
    return buildDuct(height, width, false, width - boxFrameThickness * 2, height - boxFrameThickness * 2, null, boxFrameThickness);
  }, [height, width]);

  // const espBoxGeom = useMemo(() => {
  //   return buildHollowFrame(length, height, width, boxFrameThickness);
  // }, [length, height, width]);

  const controlBoxGeom = useMemo(() => {
    return buildPanel(controlBoxLength, controlBoxHeight, controlBoxWidth - controlBoxFrontPanel);
  }, [controlBoxLength, controlBoxHeight, controlBoxWidth]);

  const controlBoxFrontPanelGeom = useMemo(() => {
    return buildPanel(controlBoxLength, controlBoxHeight, controlBoxFrontPanel);
  }, [controlBoxLength, controlBoxHeight]);

  return (
    <group dispose={null} position={position}>
      <mesh>
        <boxGeometry args={[length, height, width]} />
        <meshStandardMaterial color={"SteelBlue"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="SteelBlue" widthTest={true} />
        </Edges>
      </mesh>

      {/* <mesh geometry={espBoxGeom}>
        <meshStandardMaterial color={"SteelBlue"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="SteelBlue" widthTest={true} />
        </Edges>
      </mesh> */}
      <mesh>
        <boxGeometry args={[length - boxFrameThickness, height - boxFrameThickness, width - boxFrameThickness]} />
        <meshStandardMaterial color={"White"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="#111" widthTest={true} />
        </Edges>
      </mesh>
      <mesh geometry={controlBoxGeom} position={[0, 0, accessFactor * (width / 2 + (controlBoxWidth - controlBoxFrontPanel) / 2)]}>
        <meshStandardMaterial color={"SteelBlue"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="SteelBlue" widthTest={true} />
        </Edges>
      </mesh>
      <mesh geometry={controlBoxFrontPanelGeom} position={[0, 0, accessFactor * (width / 2 + (controlBoxWidth - controlBoxFrontPanel) + controlBoxFrontPanel / 2)]}>
        <meshStandardMaterial color={"#FFC300"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="#FFC300" widthTest={true} />
        </Edges>
      </mesh>
      <group position={[0, fromMM(20), accessFactor * (width / 2 + (controlBoxWidth - controlBoxFrontPanel) + controlBoxFrontPanel + fromMM(5) / 2)]}>
        <mesh>
          <boxGeometry args={[controlBoxFrontPanelWhite, controlBoxLength * 0.5, fromMM(5)]} />
          <meshStandardMaterial color={"White"} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
          </Edges>
        </mesh>
        <mesh
          position={[align(0, controlBoxFrontPanelWhite, controlBoxRedKnobRadius * 2, COMPONENT_ALIGNMENT.LEFT) + controlBoxFrontPanelWhite / 4, 0, fromMM(10) / 2]}
          rotation={[Math.PI / 2, 0, 0]}
        >
          <cylinderGeometry args={[controlBoxRedKnobRadius, controlBoxRedKnobRadius, fromMM(10), 32]} />
          <meshStandardMaterial color={"Red"} />
        </mesh>
        <mesh position={[align(0, controlBoxFrontPanelWhite, controlBoxDial, COMPONENT_ALIGNMENT.RIGHT) - controlBoxFrontPanelWhite / 4, 0, fromMM(10) / 2]}>
          <boxGeometry args={[controlBoxDial, controlBoxDial, fromMM(10)]} />
          <meshStandardMaterial color={"DarkGrey"} />
        </mesh>
      </group>
      <mesh position={[length / 2, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
        <extrudeGeometry args={[lipGeom, extrudeSettings]} />
        <meshStandardMaterial color={"SteelBlue"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="SteelBlue" widthTest={true} />
        </Edges>
      </mesh>
      <mesh position={[-length / 2 - lip, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
        <extrudeGeometry args={[lipGeom, extrudeSettings]} />
        <meshStandardMaterial color={"SteelBlue"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="SteelBlue" widthTest={true} />
        </Edges>
      </mesh>
      <mesh
        position={[
          align(0, length, topCylinderRadius * 2, COMPONENT_ALIGNMENT.LEFT) + fromMM(20),
          height / 2,
          align(0, width, topCylinderRadius * 2, COMPONENT_ALIGNMENT.TOP) - fromMM(20),
        ]}
      >
        <cylinderGeometry args={[topCylinderRadius, topCylinderRadius, fromMM(10), 32]} />
        <meshStandardMaterial color={"SteelBlue"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="#111" widthTest={true} />
        </Edges>
      </mesh>
      <mesh
        position={[
          align(0, length, topCylinderRadius * 2, COMPONENT_ALIGNMENT.RIGHT) - fromMM(20),
          height / 2,
          align(0, width, topCylinderRadius * 2, COMPONENT_ALIGNMENT.TOP) - fromMM(20),
        ]}
      >
        <cylinderGeometry args={[topCylinderRadius, topCylinderRadius, fromMM(10), 32]} />
        <meshStandardMaterial color={"SteelBlue"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="#111" widthTest={true} />
        </Edges>
      </mesh>
      <mesh
        position={[
          align(0, length, topCylinderRadius * 2, COMPONENT_ALIGNMENT.LEFT) + fromMM(20),
          height / 2,
          align(0, width, topCylinderRadius * 2, COMPONENT_ALIGNMENT.BOTTOM) + fromMM(20),
        ]}
      >
        <cylinderGeometry args={[topCylinderRadius, topCylinderRadius, fromMM(10), 32]} />
        <meshStandardMaterial color={"SteelBlue"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="#111" widthTest={true} />
        </Edges>
      </mesh>
      <mesh
        position={[
          align(0, length, topCylinderRadius * 2, COMPONENT_ALIGNMENT.RIGHT) - fromMM(20),
          height / 2,
          align(0, width, topCylinderRadius * 2, COMPONENT_ALIGNMENT.BOTTOM) + fromMM(20),
        ]}
      >
        <cylinderGeometry args={[topCylinderRadius, topCylinderRadius, fromMM(10), 32]} />
        <meshStandardMaterial color={"SteelBlue"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="#111" widthTest={true} />
        </Edges>
      </mesh>
    </group>
  );
}

function ESPDrain({
  baseFrameLength,
  baseFrameHeight,
  baseFrameWidth,
  frameHeight,
  align,
  IsEspExterior,
  frameThickness,
  espUnitPosition,
  espUnitWidth,
  espUnitLength,
  espUnitHeight,
  isBottomRow,
  verticalCount,
  horizontalCount,
  height,
  accessFactor = 1,
}) {
  const ESPDrainHeight = fromMM(60);
  const ESPDrainWidth = fromMM(30);
  const ESPDrainLength = fromMM(30);
  const faucetRadius = fromMM(15);
  const faucetLength = fromMM(100);
  const faucetHeight = fromMM(30);
  const footOffset = frameThickness * 1;
  const drainYPosition = espUnitPosition[1] - espUnitHeight / 2 - footOffset;
  const drainPosition = [espUnitPosition[0], drainYPosition, accessFactor * (espUnitPosition[2] + espUnitWidth / 2 - ESPDrainWidth / 2)];
  const faucetPosition = [0, 0, accessFactor * (ESPDrainWidth / 2 + faucetLength / 2)];

  return (
    <group position={drainPosition}>
      <mesh>
        <boxGeometry args={[ESPDrainLength, ESPDrainHeight, ESPDrainWidth]} />
        <meshStandardMaterial color="DarkGrey" side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" depthTest={true} />
        </Edges>
      </mesh>
      <mesh position={faucetPosition} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[faucetRadius, faucetRadius, faucetLength, 32]} />
        <meshStandardMaterial color="Silver" />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Silver" depthTest={true} />
        </Edges>
      </mesh>
    </group>
  );
}

function Feet({
  // baseFrameLength,
  // baseFrameHeight,
  // baseFrameWidth,
  frameHeight,
  align,
  isEspExterior,
  frameThickness,
  espUnitPosition,
  espUnitWidth,
  espUnitLength,
  espUnitHeight,
  isBottomRow,
  verticalCount,
  horizontalCount,
  height,
  feetHeight,
}) {
  const footHeight = feetHeight; //frameThickness * 6;
  const footTopSize = fromMM(30) * 4.6;
  const footBottomSize = footTopSize * 0.5;
  const footOffset = footHeight / 2;
  const cornerOffsetX = espUnitLength / 2 - footTopSize / 2;
  const cornerOffsetZ = espUnitWidth / 2 - footTopSize / 2;
  const footYPosition = espUnitPosition[1] - espUnitHeight / 2 - footOffset;
  const baseYPosition = espUnitPosition[1] - espUnitHeight / 2;

  const footGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();

    const vertices = new Float32Array([
      -footBottomSize / 2,
      -footHeight / 2,
      -footBottomSize / 2,
      footBottomSize / 2,
      -footHeight / 2,
      -footBottomSize / 2,
      footBottomSize / 2,
      -footHeight / 2,
      footBottomSize / 2,
      -footBottomSize / 2,
      -footHeight / 2,
      footBottomSize / 2,
      -footTopSize / 2,
      footHeight / 2,
      -footTopSize / 2,
      footTopSize / 2,
      footHeight / 2,
      -footTopSize / 2,
      footTopSize / 2,
      footHeight / 2,
      footTopSize / 2,
      -footTopSize / 2,
      footHeight / 2,
      footTopSize / 2,
    ]);

    const indices = [0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7, 0, 4, 5, 0, 5, 1, 1, 5, 6, 1, 6, 2, 2, 6, 7, 2, 7, 3, 3, 7, 4, 3, 4, 0];

    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }, [footTopSize, footBottomSize, footHeight]);

  return (
    <group>
      {/* {verticalCount === 1 && horizontalCount === 1 && (
        <mesh
          position={
            hasFrame
              ? [0, baseYPosition, 0]
              : [0, baseYPosition - frameThickness, 0]
          }
        >
          <boxGeometry
            args={[baseFrameLength, baseFrameHeight, baseFrameWidth]}
          />
          <meshStandardMaterial color="Grey" />
          <Edges scale={1} renderOrder={1}>
            <meshBasicMaterial transparent color="Grey" depthTest={true} />
          </Edges>
        </mesh>
      )} */}
      {isEspExterior && isBottomRow && (
        <>
          <mesh position={[espUnitPosition[0] + cornerOffsetX, footYPosition, espUnitPosition[2] + cornerOffsetZ]}>
            <bufferGeometry attach="geometry" {...footGeometry} />
            <meshStandardMaterial color="DarkGrey" />
            <Edges scale={1} renderOrder={1}>
              <meshBasicMaterial transparent color="Grey" depthTest={true} />
            </Edges>
          </mesh>
          <mesh position={[espUnitPosition[0] - cornerOffsetX, footYPosition, espUnitPosition[2] + cornerOffsetZ]}>
            <bufferGeometry attach="geometry" {...footGeometry} />
            <meshStandardMaterial color="DarkGrey" />
            <Edges scale={1} renderOrder={1}>
              <meshBasicMaterial transparent color="Grey" depthTest={true} />
            </Edges>
          </mesh>
          <mesh position={[espUnitPosition[0] + cornerOffsetX, footYPosition, espUnitPosition[2] - cornerOffsetZ]}>
            <bufferGeometry attach="geometry" {...footGeometry} />
            <meshStandardMaterial color="DarkGrey" />
            <Edges scale={1} renderOrder={1}>
              <meshBasicMaterial transparent color="Grey" depthTest={true} />
            </Edges>
          </mesh>
          <mesh position={[espUnitPosition[0] - cornerOffsetX, footYPosition, espUnitPosition[2] - cornerOffsetZ]}>
            <bufferGeometry attach="geometry" {...footGeometry} />
            <meshStandardMaterial color="DarkGrey" />
            <Edges scale={1} renderOrder={1}>
              <meshBasicMaterial transparent color="Grey" depthTest={true} />
            </Edges>
          </mesh>
        </>
      )}
    </group>
  );
}
